import React, { useEffect, useState } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Layers, Loader2, Save, Power } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ALL_MODULES = [
  { key: 'payroll', label: 'Payroll' },
  { key: 'attendance', label: 'Attendance' },
  { key: 'leave', label: 'Leave' },
  { key: 'statutory', label: 'Statutory' },
  { key: 'employees', label: 'Employees' },
  { key: 'reports', label: 'Reports' },
  { key: 'finance', label: 'Finance' },
  { key: 'analytics', label: 'Analytics' },
];

const ModuleControl = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => { fetchTenants(); }, []);

  const fetchTenants = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/platform/module-control`, { withCredentials: true });
      setTenants(data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load tenants');
    } finally { setLoading(false); }
  };

  const toggle = (tenantId, moduleKey) => {
    setTenants(prev => prev.map(t => {
      if (t.id !== tenantId) return t;
      const set = new Set(t.enabled_modules || []);
      if (set.has(moduleKey)) set.delete(moduleKey); else set.add(moduleKey);
      return { ...t, enabled_modules: Array.from(set), _dirty: true };
    }));
  };

  const save = async (tenant) => {
    setSaving(s => ({ ...s, [tenant.id]: true }));
    try {
      await axios.patch(`${API_URL}/platform/module-control/${tenant.id}`,
        { enabled_modules: tenant.enabled_modules }, { withCredentials: true });
      toast.success(`Saved modules for ${tenant.name}`);
      setTenants(prev => prev.map(t => t.id === tenant.id ? { ...t, _dirty: false } : t));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(s => ({ ...s, [tenant.id]: false }));
    }
  };

  if (loading) {
    return <MainLayout><div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div></MainLayout>;
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="module-control-page">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
            <Layers className="inline-block w-8 h-8 mr-2 mb-1 text-[#0052FF]" />
            Module Control
          </h1>
          <p className="mt-1 text-sm text-slate-600">Enable or disable business modules per tenant. Disabled modules are hidden from that tenant's users.</p>
        </div>

        <div className="space-y-4">
          {tenants.map(t => (
            <div key={t.id} data-testid={`module-tenant-${t.code}`} className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">{t.name}</h3>
                  <p className="text-xs text-slate-500 font-mono">{t.code} · {t.status}</p>
                </div>
                <Button
                  size="sm"
                  data-testid={`save-modules-${t.code}`}
                  disabled={!t._dirty || saving[t.id]}
                  onClick={() => save(t)}
                  className="bg-[#0052FF] hover:bg-[#0043D1] text-white disabled:opacity-50"
                >
                  {saving[t.id] ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {ALL_MODULES.map(m => {
                  const enabled = (t.enabled_modules || []).includes(m.key);
                  return (
                    <button
                      key={m.key}
                      data-testid={`toggle-${t.code}-${m.key}`}
                      onClick={() => toggle(t.id, m.key)}
                      className={`flex items-center justify-between px-3 py-2 rounded-lg border text-sm transition-colors ${
                        enabled
                          ? 'bg-green-50 border-green-300 text-green-800'
                          : 'bg-slate-50 border-slate-200 text-slate-500'
                      }`}
                    >
                      <span className="font-medium">{m.label}</span>
                      <Power className={`w-3.5 h-3.5 ${enabled ? 'text-green-600' : 'text-slate-400'}`} />
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {tenants.length === 0 && <div className="text-center py-10 text-slate-400">No tenants found</div>}
        </div>
      </div>
    </MainLayout>
  );
};

export default ModuleControl;
